import { featureInteriorPoint } from "./src/map/geo.js";
import { REPORT_LOCATION_SOURCE, isValidCoordinatePair, normalizedCoordinates } from "./photo-location-state.js";

export const ARIZONA_REPORT_LOCATION_FALLBACK = Object.freeze({
  latitude: 34.25,
  longitude: -111.9,
});

export function reportLocationFeatureZipCode(feature) {
  return String(feature?.properties?.GEOID || feature?.properties?.ZCTA5 || "");
}

export function zipFeatureForCode(zipGeometry, zipCode) {
  const normalizedZip = String(zipCode || "").trim();
  if (!/^\d{5}$/.test(normalizedZip)) return null;
  return (zipGeometry?.features || []).find((feature) => reportLocationFeatureZipCode(feature) === normalizedZip) || null;
}

export function coordinatesFromFeatureInteriorPoint(feature) {
  if (!feature) return null;
  const [longitude, latitude] = featureInteriorPoint(feature);
  return normalizedCoordinates({ latitude, longitude });
}

export function reportLocationCoordinatesForZip(zipGeometry, zipCode) {
  return coordinatesFromFeatureInteriorPoint(zipFeatureForCode(zipGeometry, zipCode));
}

export function featureContainsCoordinate(feature, coords) {
  const coordinates = normalizedCoordinates(coords);
  if (!coordinates) return false;
  const geometry = feature?.geometry;
  if (geometry?.type === "Polygon") {
    return polygonContainsCoordinate(geometry.coordinates || [], coordinates);
  }
  if (geometry?.type === "MultiPolygon") {
    return (geometry.coordinates || []).some((polygon) => polygonContainsCoordinate(polygon, coordinates));
  }
  return false;
}

export function polygonContainsCoordinate(polygon, { latitude, longitude }) {
  if (!Array.isArray(polygon) || polygon.length === 0) return false;
  const [outerRing, ...holes] = polygon;
  if (!pointInRing(longitude, latitude, outerRing)) return false;
  return !holes.some((ring) => pointInRing(longitude, latitude, ring));
}

export function pointInRing(lng, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 4) return false;
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const xi = Number(ring[index]?.[0]);
    const yi = Number(ring[index]?.[1]);
    const xj = Number(ring[previous]?.[0]);
    const yj = Number(ring[previous]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
    const intersects = ((yi > lat) !== (yj > lat)) && (
      lng < ((xj - xi) * (lat - yi)) / (yj - yi || Number.EPSILON) + xi
    );
    if (intersects) inside = !inside;
  }
  return inside;
}

export function initialReportLocationCoordinates({ browserCoordinates = null, zipGeometry = null, zipCode = "" } = {}) {
  const browser = normalizedCoordinates(browserCoordinates);
  if (browser) {
    return {
      coordinates: browser,
      sourceType: REPORT_LOCATION_SOURCE.BROWSER,
      sourceZip: "",
    };
  }

  const zipCoordinates = reportLocationCoordinatesForZip(zipGeometry, zipCode);
  if (zipCoordinates) {
    return {
      coordinates: zipCoordinates,
      sourceType: REPORT_LOCATION_SOURCE.ZIP,
      sourceZip: String(zipCode || ""),
    };
  }

  return {
    coordinates: ARIZONA_REPORT_LOCATION_FALLBACK,
    sourceType: REPORT_LOCATION_SOURCE.ARIZONA_FALLBACK,
    sourceZip: "",
  };
}

export function reportLocationHasValidStartingPoint(candidate) {
  return isValidCoordinatePair(candidate?.coordinates);
}
